import type { ChecklistTemplate } from './types';

/**
 * Listas de verificación por profesión.
 *
 * Lo que un profesional quiere saber la mayoría de las veces es **qué
 * documentos necesita para esto** y **qué le falta todavía**. Eso no es
 * inteligencia artificial: es una lista, y el catálogo de perfiles ya sabe
 * guardar cosas por profesión.
 *
 * ---
 *
 * **Por qué viven aquí y no dentro de `WorkspaceProfile`.**
 *
 * La tentación era añadir un campo `checklists` al perfil, junto al árbol de
 * carpetas. Se descartó por una razón práctica: obligaría a tocar los
 * veinticinco perfiles repartidos en tres archivos para añadir un dato que
 * ninguno de ellos necesita para funcionar. Un mapa indexado por código de
 * perfil dice exactamente lo mismo, deja los perfiles intactos y permite que
 * un perfil tenga varias listas sin ensanchar su definición.
 *
 * El precio es que los dos catálogos pueden separarse —una lista apuntando a
 * un perfil que ya no existe, o un perfil sin ninguna lista— y por eso
 * `tests/catalog-consistency.test.ts` comprueba ambas direcciones.
 *
 * ---
 *
 * **No se siembran en la base de datos.** A diferencia de los perfiles, que se
 * registran en `workspace_profiles` para poder retirarlos sin desplegar, estas
 * plantillas son sugerencias que sólo existen en el momento de crear una lista.
 * Lo que se guarda es la **copia** del titular, en `workspace_checklists`. Por
 * eso no hay seed que mantener al día ni migración que escribir al añadir una.
 */
export const CHECKLIST_TEMPLATES: Record<string, ChecklistTemplate[]> = {
  // ---------------------------------------------------------------------------
  // Personal y familia
  // ---------------------------------------------------------------------------
  PERSONAL_GENERAL: [
    {
      code: 'PERSONAL_HOUSEHOLD_DOCS',
      profileCode: 'PERSONAL_GENERAL',
      name: 'Documentos importantes del hogar',
      description: 'Lo que conviene tener localizado antes de necesitarlo con prisa.',
      items: [
        { key: 'id_document', label: 'Documento de identidad' },
        { key: 'passport', label: 'Pasaporte', required: false },
        { key: 'home_title', label: 'Escritura o contrato de arrendamiento' },
        { key: 'utility_bills', label: 'Recibos de servicios', hint: 'Los últimos tres meses' },
        { key: 'home_insurance', label: 'Póliza de seguro del hogar' },
        { key: 'tax_return', label: 'Declaración de impuestos del último año' },
        { key: 'medical_records', label: 'Carné de vacunación y seguro médico' },
        { key: 'warranties', label: 'Garantías y facturas de electrodomésticos', required: false },
      ],
    },
  ],

  FAMILY: [
    {
      code: 'FAMILY_CORE_DOCS',
      profileCode: 'FAMILY',
      name: 'Documentos de la familia',
      description: 'Un juego por persona, para no buscarlos el día que hacen falta.',
      items: [
        { key: 'id_documents', label: 'Documento de identidad de cada miembro' },
        { key: 'birth_certificates', label: 'Registro civil de nacimiento' },
        {
          key: 'marriage_certificate',
          label: 'Acta de matrimonio o libro de familia',
          required: false,
        },
        { key: 'vaccination_cards', label: 'Carné de vacunación' },
        { key: 'health_insurance', label: 'Seguro médico familiar' },
        { key: 'school_enrollment', label: 'Matrícula escolar del curso', required: false },
        { key: 'emergency_contacts', label: 'Contactos de emergencia' },
        { key: 'wills', label: 'Testamento o poderes', required: false },
      ],
    },
  ],

  PHOTOGRAPHY: [
    {
      code: 'PHOTOGRAPHY_SESSION_DELIVERY',
      profileCode: 'PHOTOGRAPHY',
      name: 'Entrega de una sesión',
      description: 'Lo que cierra un encargo: lo pactado, lo entregado y lo cobrado.',
      items: [
        { key: 'contract', label: 'Contrato firmado' },
        { key: 'image_rights', label: 'Cesión de derechos de imagen' },
        { key: 'brief', label: 'Brief de la sesión', required: false },
        { key: 'client_selection', label: 'Selección del cliente' },
        { key: 'edited_high_res', label: 'Fotografías editadas en alta resolución' },
        { key: 'edited_web', label: 'Versiones para redes y web', required: false },
        { key: 'invoice', label: 'Factura' },
        { key: 'raw_backup', label: 'Copia de seguridad de los originales' },
      ],
    },
  ],

  STUDENT: [
    {
      code: 'STUDENT_ENROLLMENT',
      profileCode: 'STUDENT',
      name: 'Matrícula y trámites académicos',
      description: 'Lo que suele pedir una universidad o instituto al matricularse.',
      items: [
        { key: 'id_document', label: 'Documento de identidad' },
        { key: 'previous_studies', label: 'Certificado de estudios previos' },
        { key: 'transcript', label: 'Notas del último periodo' },
        { key: 'enrollment_form', label: 'Formulario de matrícula firmado' },
        { key: 'payment_receipt', label: 'Comprobante de pago' },
        { key: 'photo', label: 'Fotografía tipo carné' },
        { key: 'medical_certificate', label: 'Certificado médico', required: false },
        { key: 'scholarship', label: 'Solicitud de beca o ayuda', required: false },
      ],
    },
  ],

  // ---------------------------------------------------------------------------
  // Ingeniería, tecnología y construcción
  // ---------------------------------------------------------------------------
  CIVIL_ENGINEERING: [
    {
      code: 'CIVIL_PROJECT_KICKOFF',
      profileCode: 'CIVIL_ENGINEERING',
      name: 'Apertura de proyecto de obra',
      description: 'Lo que debe existir antes de la primera visita a obra.',
      items: [
        { key: 'contract', label: 'Contrato u orden de servicio' },
        { key: 'building_permit', label: 'Licencia de construcción' },
        { key: 'soil_study', label: 'Estudio de suelos' },
        { key: 'approved_drawings', label: 'Planos arquitectónicos aprobados' },
        { key: 'structural_calc', label: 'Memoria de cálculo estructural' },
        { key: 'budget', label: 'Presupuesto y análisis de precios unitarios' },
        { key: 'schedule', label: 'Cronograma de obra' },
        { key: 'bonds', label: 'Pólizas de cumplimiento vigentes' },
      ],
    },
  ],

  ARCHITECTURE: [
    {
      code: 'ARCHITECTURE_CLIENT_INTAKE',
      profileCode: 'ARCHITECTURE',
      name: 'Información inicial del cliente',
      description: 'Sin esto no se puede empezar a dibujar, y se pide siempre lo mismo.',
      items: [
        { key: 'property_title', label: 'Escritura o certificado de tradición del predio' },
        { key: 'survey', label: 'Levantamiento topográfico' },
        { key: 'zoning', label: 'Norma urbanística aplicable' },
        { key: 'requirements', label: 'Programa de necesidades' },
        { key: 'budget_range', label: 'Presupuesto disponible' },
        { key: 'references', label: 'Referencias e imágenes de estilo', required: false },
        { key: 'contract', label: 'Contrato firmado' },
      ],
    },
  ],

  CONSTRUCTION: [
    {
      code: 'CONSTRUCTION_SUBCONTRACTOR',
      profileCode: 'CONSTRUCTION',
      name: 'Habilitación de subcontratista',
      description: 'Lo que hay que tener archivado antes de dejar entrar a nadie a la obra.',
      items: [
        { key: 'company_registration', label: 'Registro mercantil o cámara de comercio' },
        { key: 'tax_id', label: 'Identificación fiscal' },
        { key: 'social_security', label: 'Certificado de pago de seguridad social' },
        { key: 'insurance', label: 'Pólizas vigentes' },
        { key: 'staff_cvs', label: 'Hojas de vida del personal asignado' },
        { key: 'height_training', label: 'Certificados de trabajo en alturas', required: false },
        { key: 'risk_matrix', label: 'Matriz de riesgos de la actividad' },
        { key: 'contract', label: 'Contrato firmado' },
      ],
    },
  ],

  ELECTRICAL_ENGINEERING: [
    {
      code: 'ELECTRICAL_COMMISSIONING',
      profileCode: 'ELECTRICAL_ENGINEERING',
      name: 'Puesta en servicio de una instalación',
      description: 'El expediente que respalda la energización.',
      items: [
        { key: 'single_line', label: 'Diagrama unifilar aprobado' },
        { key: 'calculations', label: 'Memoria de cálculo' },
        { key: 'conformity', label: 'Certificado de conformidad de la instalación' },
        { key: 'test_protocols', label: 'Protocolos de pruebas' },
        { key: 'equipment_certs', label: 'Certificados de los equipos instalados' },
        { key: 'grounding', label: 'Medición de puesta a tierra' },
        { key: 'as_built', label: 'Planos as-built' },
        { key: 'energization_record', label: 'Acta de energización' },
      ],
    },
  ],

  MECHANICAL_INDUSTRIAL: [
    {
      code: 'MECHANICAL_EQUIPMENT_HANDOVER',
      profileCode: 'MECHANICAL_INDUSTRIAL',
      name: 'Entrega de un equipo fabricado',
      description: 'La carpeta que acompaña al equipo cuando sale del taller.',
      items: [
        { key: 'shop_drawings', label: 'Planos de fabricación' },
        { key: 'material_certs', label: 'Certificados de material' },
        { key: 'ndt_report', label: 'Informe de ensayos no destructivos', required: false },
        { key: 'fat_protocol', label: 'Protocolo de pruebas en fábrica' },
        { key: 'manual', label: 'Manual de operación y mantenimiento' },
        { key: 'spare_parts', label: 'Lista de repuestos recomendados' },
        { key: 'warranty', label: 'Certificado de garantía' },
        { key: 'handover_record', label: 'Acta de entrega firmada' },
      ],
    },
  ],

  TELECOM_ENGINEERING: [
    {
      code: 'TELECOM_SITE_ACCEPTANCE',
      profileCode: 'TELECOM_ENGINEERING',
      name: 'Acta de sitio',
      description: 'Lo que el operador pide para dar por recibido un emplazamiento.',
      items: [
        { key: 'site_sheet', label: 'Ficha del emplazamiento' },
        { key: 'photos', label: 'Reportaje fotográfico de la instalación' },
        {
          key: 'measurements',
          label: 'Mediciones',
          hint: 'OTDR, drive test o las del tipo de sitio',
        },
        { key: 'equipment_inventory', label: 'Inventario de equipos instalados' },
        { key: 'configuration', label: 'Configuración y puesta en marcha' },
        { key: 'site_permit', label: 'Permiso del propietario del sitio' },
        { key: 'grounding', label: 'Certificado de puesta a tierra' },
        { key: 'acceptance_record', label: 'Acta firmada' },
      ],
    },
  ],

  SOFTWARE_IT: [
    {
      code: 'SOFTWARE_PROJECT_KICKOFF',
      profileCode: 'SOFTWARE_IT',
      name: 'Arranque de proyecto',
      description: 'Lo que evita empezar a programar sobre supuestos.',
      items: [
        { key: 'contract', label: 'Contrato o pliego de trabajo' },
        { key: 'scope', label: 'Alcance y requerimientos acordados' },
        {
          key: 'access_inventory',
          label: 'Inventario de accesos necesarios',
          hint: 'Qué sistemas y con qué permisos. Las contraseñas nunca se suben aquí.',
        },
        { key: 'architecture', label: 'Diagrama de arquitectura', required: false },
        { key: 'deployment_plan', label: 'Plan de despliegue' },
        { key: 'sla', label: 'Acuerdo de nivel de servicio', required: false },
        { key: 'contacts', label: 'Contacto técnico del cliente' },
        { key: 'backup_plan', label: 'Plan de copias de seguridad' },
      ],
    },
  ],

  ENVIRONMENTAL_HSE: [
    {
      code: 'HSE_INSPECTION_FILE',
      profileCode: 'ENVIRONMENTAL_HSE',
      name: 'Expediente de inspección',
      description: 'Lo que pide una autoridad o una auditoría cuando llega sin avisar.',
      items: [
        { key: 'hazard_matrix', label: 'Matriz de identificación de peligros' },
        { key: 'permits', label: 'Permisos ambientales vigentes' },
        { key: 'waste_disposal', label: 'Certificados de disposición de residuos' },
        { key: 'training_records', label: 'Registros de capacitación' },
        { key: 'ppe_delivery', label: 'Entrega de elementos de protección personal' },
        { key: 'incident_reports', label: 'Reportes de incidentes del periodo' },
        { key: 'emergency_plan', label: 'Plan de emergencias' },
        { key: 'committee_minutes', label: 'Actas del comité', required: false },
      ],
    },
  ],

  ENGINEERING_GENERAL: [
    {
      code: 'ENGINEERING_PROJECT_KICKOFF',
      profileCode: 'ENGINEERING_GENERAL',
      name: 'Apertura de proyecto',
      description: 'La base que sirve a cualquier especialidad antes de arrancar.',
      items: [
        { key: 'contract', label: 'Contrato u orden de servicio' },
        { key: 'scope', label: 'Alcance técnico' },
        { key: 'standards', label: 'Normativa aplicable' },
        { key: 'reference_docs', label: 'Planos y memorias de referencia' },
        { key: 'schedule', label: 'Cronograma' },
        { key: 'budget', label: 'Presupuesto aprobado' },
        { key: 'contacts', label: 'Contactos del cliente' },
      ],
    },
  ],

  // ---------------------------------------------------------------------------
  // Servicios profesionales
  // ---------------------------------------------------------------------------
  LEGAL: [
    {
      code: 'LEGAL_CASE_INTAKE',
      profileCode: 'LEGAL',
      name: 'Apertura de expediente',
      description: 'Lo que se le pide al cliente en la primera reunión.',
      items: [
        { key: 'client_id', label: 'Documento de identidad del cliente' },
        { key: 'power_of_attorney', label: 'Poder firmado' },
        { key: 'engagement_letter', label: 'Contrato de prestación de servicios' },
        { key: 'facts', label: 'Relación de hechos y antecedentes' },
        { key: 'evidence', label: 'Pruebas documentales' },
        { key: 'prior_filings', label: 'Actuaciones previas del proceso', required: false },
        { key: 'counterparty', label: 'Datos de la contraparte' },
        { key: 'fee_receipt', label: 'Comprobante de honorarios', required: false },
      ],
    },
    {
      code: 'LEGAL_CONTRACT_SIGNING',
      profileCode: 'LEGAL',
      name: 'Firma de un contrato',
      description: 'Lo que debe quedar archivado el día que se firma.',
      items: [
        { key: 'final_draft', label: 'Versión final revisada' },
        { key: 'signed_copy', label: 'Ejemplar firmado por todas las partes' },
        { key: 'party_ids', label: 'Identificación de los firmantes' },
        { key: 'corporate_authority', label: 'Documento que acredita la representación' },
        { key: 'annexes', label: 'Anexos referenciados en el contrato' },
        { key: 'notarization', label: 'Escritura o autenticación notarial', required: false },
        { key: 'key_dates', label: 'Ficha de plazos y vencimientos' },
      ],
    },
  ],

  ACCOUNTING: [
    {
      code: 'ACCOUNTING_MONTHLY_CLOSE',
      profileCode: 'ACCOUNTING',
      name: 'Cierre mensual del cliente',
      description: 'Lo que hay que reclamar cada mes, cliente por cliente.',
      items: [
        { key: 'bank_statements', label: 'Extractos bancarios' },
        { key: 'sales_invoices', label: 'Facturas de venta' },
        { key: 'purchase_invoices', label: 'Facturas de compra' },
        { key: 'payroll', label: 'Nómina del periodo' },
        { key: 'withholdings', label: 'Comprobantes de retención' },
        { key: 'inventory', label: 'Inventario', required: false },
        { key: 'bank_reconciliation', label: 'Conciliación bancaria' },
        { key: 'filed_return', label: 'Declaración presentada' },
      ],
    },
    {
      code: 'ACCOUNTING_CLIENT_ONBOARDING',
      profileCode: 'ACCOUNTING',
      name: 'Alta de un cliente nuevo',
      description: 'Lo que se pide una sola vez, al empezar la relación.',
      items: [
        { key: 'tax_id', label: 'Identificación fiscal' },
        { key: 'company_registration', label: 'Registro mercantil' },
        { key: 'bylaws', label: 'Estatutos y actas de constitución' },
        { key: 'legal_rep_id', label: 'Documento del representante legal' },
        { key: 'opening_balance', label: 'Balance de apertura' },
        { key: 'prior_returns', label: 'Declaraciones de los periodos anteriores' },
        { key: 'engagement_letter', label: 'Contrato de servicios firmado' },
        { key: 'bank_authorization', label: 'Autorización de consulta bancaria', required: false },
      ],
    },
  ],

  AUDIT: [
    {
      code: 'AUDIT_PBC_REQUEST',
      profileCode: 'AUDIT',
      name: 'Solicitud inicial de información',
      description: 'La lista que se envía al auditado al empezar el encargo.',
      items: [
        { key: 'financial_statements', label: 'Estados financieros del periodo' },
        { key: 'trial_balance', label: 'Balance de comprobación' },
        { key: 'ledgers', label: 'Libros auxiliares' },
        { key: 'board_minutes', label: 'Actas de junta y asamblea' },
        { key: 'bank_reconciliations', label: 'Conciliaciones bancarias' },
        { key: 'contracts', label: 'Contratos vigentes' },
        { key: 'confirmations', label: 'Confirmaciones de saldos', required: false },
        { key: 'representation_letter', label: 'Carta de representación' },
      ],
    },
  ],

  CONSULTING: [
    {
      code: 'CONSULTING_ENGAGEMENT_START',
      profileCode: 'CONSULTING',
      name: 'Arranque de un encargo',
      description: 'Lo que convierte una propuesta aceptada en un proyecto en marcha.',
      items: [
        { key: 'accepted_proposal', label: 'Propuesta aceptada' },
        { key: 'contract', label: 'Contrato firmado' },
        { key: 'kickoff_minutes', label: 'Acta de inicio' },
        { key: 'work_plan', label: 'Plan de trabajo' },
        { key: 'stakeholders', label: 'Matriz de interesados', required: false },
        { key: 'data_access', label: 'Datos y accesos acordados' },
        { key: 'report_template', label: 'Plantilla de informe', required: false },
        { key: 'billing_schedule', label: 'Calendario de facturación' },
      ],
    },
  ],

  // ---------------------------------------------------------------------------
  // Empresa y operaciones
  // ---------------------------------------------------------------------------
  GENERAL_BUSINESS: [
    {
      code: 'BUSINESS_SUPPLIER_ONBOARDING',
      profileCode: 'GENERAL_BUSINESS',
      name: 'Alta de proveedor',
      description: 'Lo que hay que tener antes de emitir la primera orden de compra.',
      items: [
        { key: 'company_registration', label: 'Registro mercantil' },
        { key: 'tax_id', label: 'Identificación fiscal' },
        { key: 'bank_certificate', label: 'Certificación bancaria' },
        { key: 'references', label: 'Referencias comerciales', required: false },
        { key: 'framework_agreement', label: 'Contrato o acuerdo marco' },
        { key: 'social_security', label: 'Certificado de seguridad social', required: false },
        { key: 'liability_insurance', label: 'Póliza de responsabilidad civil', required: false },
        { key: 'contacts', label: 'Datos de contacto y facturación' },
      ],
    },
  ],

  HR_RECRUITING: [
    {
      code: 'HR_NEW_HIRE',
      profileCode: 'HR_RECRUITING',
      name: 'Contratación de una persona',
      description: 'El expediente que se abre el primer día.',
      items: [
        { key: 'id_document', label: 'Documento de identidad' },
        { key: 'resume', label: 'Hoja de vida' },
        { key: 'diplomas', label: 'Diplomas y certificados de estudios' },
        { key: 'work_certificates', label: 'Certificados laborales' },
        { key: 'references', label: 'Referencias verificadas', required: false },
        { key: 'medical_exam', label: 'Examen médico de ingreso' },
        { key: 'social_security', label: 'Afiliación a seguridad social' },
        { key: 'signed_contract', label: 'Contrato firmado' },
        { key: 'bank_details', label: 'Datos bancarios para la nómina' },
      ],
    },
  ],

  REAL_ESTATE: [
    {
      code: 'REAL_ESTATE_LISTING',
      profileCode: 'REAL_ESTATE',
      name: 'Documentos para poner un inmueble en venta',
      description: 'Sin esto no se puede escriturar, y se descubre tarde.',
      items: [
        { key: 'deed', label: 'Escritura pública' },
        { key: 'title_certificate', label: 'Certificado de tradición y libertad' },
        { key: 'property_tax', label: 'Paz y salvo de impuesto predial' },
        { key: 'hoa_clearance', label: 'Paz y salvo de administración', required: false },
        { key: 'utility_bills', label: 'Recibos de servicios al día' },
        { key: 'owner_id', label: 'Documento de identidad del propietario' },
        { key: 'appraisal', label: 'Avalúo comercial', required: false },
        { key: 'photos', label: 'Fotografías del inmueble' },
      ],
    },
  ],

  INSURANCE: [
    {
      code: 'INSURANCE_CLAIM',
      profileCode: 'INSURANCE',
      name: 'Reclamación de siniestro',
      description: 'Lo que la aseguradora va a pedir, pedido de una sola vez.',
      items: [
        { key: 'claim_form', label: 'Formulario de reclamación' },
        { key: 'policy', label: 'Póliza vigente' },
        { key: 'insured_id', label: 'Documento de identidad del asegurado' },
        { key: 'authority_report', label: 'Denuncia o informe de autoridad', required: false },
        { key: 'damage_photos', label: 'Fotografías del daño' },
        { key: 'repair_quotes', label: 'Facturas y presupuestos de reparación' },
        { key: 'adjuster_report', label: 'Informe del perito', required: false },
        { key: 'bank_certificate', label: 'Certificación bancaria para el pago' },
      ],
    },
  ],

  // ---------------------------------------------------------------------------
  // Salud
  // ---------------------------------------------------------------------------
  HEALTHCARE: [
    {
      code: 'HEALTHCARE_PATIENT_INTAKE',
      profileCode: 'HEALTHCARE',
      name: 'Ingreso de paciente',
      description:
        'Sólo lo necesario para atender. Cuanto menos dato clínico se guarde fuera de la historia, mejor.',
      items: [
        { key: 'id_document', label: 'Documento de identidad' },
        { key: 'insurance_card', label: 'Carné del seguro o asegurador' },
        { key: 'informed_consent', label: 'Consentimiento informado firmado' },
        { key: 'prior_history', label: 'Historia clínica previa', required: false },
        { key: 'recent_tests', label: 'Exámenes recientes', required: false },
        { key: 'prescriptions', label: 'Fórmulas médicas vigentes', required: false },
        { key: 'authorization', label: 'Autorización del asegurador' },
        { key: 'emergency_contact', label: 'Contacto de emergencia' },
      ],
    },
  ],

  // ---------------------------------------------------------------------------
  // Creatividad y medios
  // ---------------------------------------------------------------------------
  CREATIVE: [
    {
      code: 'CREATIVE_CAMPAIGN_KICKOFF',
      profileCode: 'CREATIVE',
      name: 'Arranque de campaña',
      description: 'El material del cliente sin el cual no se puede producir nada.',
      items: [
        { key: 'signed_brief', label: 'Brief firmado' },
        { key: 'brand_manual', label: 'Manual de marca' },
        { key: 'logos', label: 'Logotipos en vectorial' },
        { key: 'fonts', label: 'Tipografías con licencia', required: false },
        { key: 'assets', label: 'Material fotográfico y de vídeo', required: false },
        { key: 'approved_copy', label: 'Textos aprobados' },
        { key: 'calendar', label: 'Calendario de publicación' },
        { key: 'purchase_order', label: 'Contrato u orden de compra' },
      ],
    },
  ],

  // ---------------------------------------------------------------------------
  // Educación y organizaciones
  // ---------------------------------------------------------------------------
  EDUCATION: [
    {
      code: 'EDUCATION_STUDENT_ENROLLMENT',
      profileCode: 'EDUCATION',
      name: 'Matrícula de un estudiante',
      description: 'La carpeta que se abre por cada estudiante al inicio del curso.',
      items: [
        { key: 'student_id', label: 'Documento de identidad del estudiante' },
        { key: 'guardian_id', label: 'Documento del acudiente o tutor' },
        { key: 'previous_certificates', label: 'Certificados de años anteriores' },
        { key: 'report_card', label: 'Boletín de notas' },
        { key: 'vaccination_card', label: 'Carné de vacunación', required: false },
        { key: 'photo', label: 'Fotografía tipo carné' },
        { key: 'enrollment_form', label: 'Formulario de matrícula firmado' },
        { key: 'payment_receipt', label: 'Comprobante de pago' },
      ],
    },
  ],

  NONPROFIT: [
    {
      code: 'NONPROFIT_PROJECT_REPORT',
      profileCode: 'NONPROFIT',
      name: 'Rendición de cuentas de un proyecto',
      description: 'Lo que exige un donante al cerrar, y conviene ir juntando desde el principio.',
      items: [
        { key: 'agreement', label: 'Convenio o contrato de donación' },
        { key: 'approved_budget', label: 'Presupuesto aprobado' },
        { key: 'narrative_report', label: 'Informe narrativo' },
        { key: 'financial_report', label: 'Informe financiero' },
        { key: 'expense_receipts', label: 'Soportes de gasto' },
        { key: 'beneficiaries', label: 'Registro de beneficiarios' },
        { key: 'activity_photos', label: 'Fotografías de las actividades', required: false },
        { key: 'closing_minutes', label: 'Acta de cierre' },
      ],
    },
  ],
};

/** Todas las plantillas del catálogo, sin agrupar. */
export const ALL_CHECKLIST_TEMPLATES: ChecklistTemplate[] =
  Object.values(CHECKLIST_TEMPLATES).flat();

const TEMPLATE_BY_CODE = new Map(
  ALL_CHECKLIST_TEMPLATES.map((template) => [template.code, template]),
);

/**
 * Listas sugeridas para un perfil.
 *
 * Devuelve un array vacío —nunca `undefined`— para que quien la use no tenga
 * que distinguir «perfil sin listas» de «perfil que no existe»: en ambos casos
 * no hay nada que sugerir.
 */
export function listChecklistTemplates(
  profileCode: string | null | undefined,
): ChecklistTemplate[] {
  if (!profileCode) return [];
  return CHECKLIST_TEMPLATES[profileCode] ?? [];
}

export function getChecklistTemplate(code: string | null | undefined): ChecklistTemplate | null {
  if (!code) return null;
  return TEMPLATE_BY_CODE.get(code) ?? null;
}
